import { api } from '../api';
import {
  GET_MARKETS,
  GET_TICKERS,
  GET_AGGTRADES,
  GET_P2P_MARKET_DEPTH,
  ORDER_HISTORY,
  GET_OPEN_ORDERS,
  INIT_ORDER,
  GET_KLINE,
  CANCEL_ORDER
} from '../../constants';
import {
  GetMarketResponse,
  GetTickerResponse,
  GetAggTradeResponse,
  OrderHistoryResponse,
  OrderHistoryRequest,
  GetMarketDepthResponse,
  InitOrderResponse,
  InitOrderReqBody,
  KlineRequest,
  KlineResponse,
  OpenOrderHistoryResponse,
  CancelOrderReqBody,
  CancelOrderResponse
} from './types';

export const p2pApiSlice = api.injectEndpoints({
  endpoints: (build) => ({
    getMarkets: build.query<GetMarketResponse, void>({
      query: () => {
        return {
          url: GET_MARKETS,
          method: 'POST',
          body: {}
        };
      },
      providesTags: ['getMarkets']
    }),
    getKline: build.query<KlineResponse, KlineRequest>({
      query: (data) => ({
        url: GET_KLINE,
        params: {
          symbol: data.symbol,
          interval: data.interval,
          limit: data.limit,
          endTime: data.endTime,
          provider: data.provider
        }
      })
    }),
    getTickers: build.query<GetTickerResponse, string | null>({
      query: (symbol) => `${GET_TICKERS}${symbol ? `?symbol=${symbol}` : ''}`,
      providesTags: ['getTickers']
    }),
    getAggTrades: build.query<GetAggTradeResponse, string | undefined | null>({
      query: (symbol) => `${GET_AGGTRADES}${symbol ? `?symbol=${symbol}` : ''}`,
      providesTags: ['getAggTrades']
    }),
    getMarketDepth: build.query<GetMarketDepthResponse, string>({
      query: (symbol: string) => ({
        url: `${GET_P2P_MARKET_DEPTH}?symbol=${symbol}`
      }),
      transformResponse: (response: GetMarketDepthResponse, meta, symbol) => {
        if (!response.data) {
          return {
            data: {
              symbol,
              askQty: '0',
              bidQty: '0',
              asks: [],
              bids: []
            }
          };
        }
        return response;
      }
    }),
    getOpenOrders: build.query<
      OpenOrderHistoryResponse,
      { page: string; pageLimit: string }
    >({
      query: ({ page, pageLimit }) => ({
        url: `${GET_OPEN_ORDERS}/${page}/${pageLimit}`
      }),
      transformResponse: (response: OpenOrderHistoryResponse, meta, arg) => {
        Object.assign(response.data, {
          ...response.data,
          page: Number(arg.page)
        });
        return response;
      },
      merge: (currentCache, newItems) => {
        const isFullyUpdated = newItems.data.page === 0;
        if (isFullyUpdated) {
          currentCache.data = newItems.data;
        } else {
          currentCache?.data?.rows?.push(...newItems.data.rows);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      providesTags: ['getOpenOrders']
    }),
    orderHistory: build.query<OrderHistoryResponse, OrderHistoryRequest>({
      query(data) {
        return {
          url: ORDER_HISTORY,
          method: 'POST',
          body: data
        };
      },
      transformResponse: (response: OrderHistoryResponse, meta, arg) => {
        Object.assign(response.data, {
          ...response.data,
          page: Number(arg.page)
        });
        return response;
      },
      providesTags: ['orderHistory'],
      keepUnusedDataFor: 100000,
      merge: (currentCache, newItems) => {
        const isFullyUpdated = newItems.data.page === 0;
        if (isFullyUpdated) {
          currentCache.data = newItems.data;
        } else {
          currentCache?.data?.rows?.push(...newItems.data.rows);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      }
    }),
    initOrder: build.mutation<InitOrderResponse, InitOrderReqBody>({
      query(data) {
        return {
          url: INIT_ORDER,
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['GetAccountingBalanceFilter', 'UserAssets']
    }),
    cancelOrder: build.mutation<CancelOrderResponse, CancelOrderReqBody>({
      query(data) {
        return {
          url: CANCEL_ORDER,
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['GetAccountingBalanceFilter', 'UserAssets']
    })
  }),
  overrideExisting: false
});

export const {
  useGetMarketsQuery,
  useGetTickersQuery,
  useGetAggTradesQuery,
  useGetMarketDepthQuery,
  useLazyOrderHistoryQuery,
  useGetOpenOrdersQuery,
  useLazyGetOpenOrdersQuery,
  useInitOrderMutation,
  useGetKlineQuery,
  useLazyGetKlineQuery,
  useCancelOrderMutation
} = p2pApiSlice;
